import { Commission } from "@/app/entities/Team";
import { useDefaultGoals } from "@/app/hooks/useDefaultGoals";
import { teamsService } from "@/app/services/teamsService";
import { UpdateTeamdata } from "@/app/services/teamsService/updateTeam";
import { useToast } from "@/view/components/ui/use-toast";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useParams } from "react-router-dom";
import { getProductsConfig } from "@/app/services/configService/getProductsConfig";
import { sellerService } from "@/app/services/sellersService";
import { PartnerType } from "@/app/utils/partners.enum";
import { tableService } from "@/app/services/tableService";

export const useEditTablesController = (
  initialCommission: Commission[],
  supervisorId?: string,
) => {
  const { id } = useParams();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { tables } = useDefaultGoals();

  const { data: productsConfig } = useQuery({
    queryKey: ["productsConfig"],
    queryFn: getProductsConfig,
  });

  const { data: supervisorData } = useQuery({
    queryKey: ["supervisorInfo", supervisorId],
    queryFn: () => sellerService.sellerInfo(supervisorId!),
    enabled: !!supervisorId,
  });

  const { data: cltTablesData } = useQuery({
    queryKey: ["allTables", { type: "CLT", limit: 99 }],
    queryFn: () => tableService.table({ type: "CLT", limit: 99 }),
  });

  const { data: crefazTablesData } = useQuery({
    queryKey: ["allTables", { type: "CREFAZ", limit: 99 }],
    queryFn: () => tableService.table({ type: "CREFAZ", limit: 99 }),
  });

  const { data: salvcarTablesData } = useQuery({
    queryKey: ["allTables", { type: "SALVCAR", limit: 99 }],
    queryFn: () => tableService.table({ type: "SALVCAR", limit: 99 }),
  });

  const { data: panTablesData } = useQuery({
    queryKey: ["allTables", { type: "PAN", limit: 99 }],
    queryFn: () => tableService.table({ type: "PAN", limit: 99 }),
  });

  const { data: crefisaTablesData } = useQuery({
    queryKey: ["allTables", { type: "CREFISA", limit: 99 }],
    queryFn: () => tableService.table({ type: "CREFISA", limit: 99 }),
  });

  const { data: c6TablesData } = useQuery({
    queryKey: ["allTables", { type: "C6", limit: 99 }],
    queryFn: () => tableService.table({ type: "C6", limit: 99 }),
  });
  const { data: crefisaInssTablesData } = useQuery({
    queryKey: ["allTables", { type: "CREFISA_INSS", limit: 99 }],
    queryFn: () => tableService.table({ type: "CREFISA_INSS", limit: 99 }),
  });

  const { data: crefazCltTablesData } = useQuery({
    queryKey: ["allTables", { type: "CREFAZ_CLT", limit: 99 }],
    queryFn: () => tableService.table({ type: "CREFAZ_CLT", limit: 99 }),
  });

  const [open, setOpen] = useState(false);
  const [localCommission, setLocalCommission] = useState<Commission[]>(
    initialCommission || [],
  );

  const defaultTablesCommission = Array.isArray(tables?.pages?.[0]?.tables)
    ? tables.pages[0].tables
        .filter((table) => table.isActive !== false)
        .map((table) => {
          const match = initialCommission?.find((c) => c.name === table.name);
          return {
            label: table.name,
            value: String(table.maxPercents),
            leader: match?.leader,
            seller: match?.seller,
            supervisor: match?.supervisor,
            max: table?.maxPercents,
          };
        })
    : [];

  const cltTables = cltTablesData?.tables || [];
  const crefazTables = crefazTablesData?.tables || [];
  const salvcarTables = salvcarTablesData?.tables || [];
  const panTables = panTablesData?.tables || [];
  const crefisaTables = crefisaTablesData?.tables || [];
  const c6Tables = c6TablesData?.tables || [];
  const crefisaInssTables = crefisaInssTablesData?.tables || [];
  const crefazCltTables = crefazCltTablesData?.tables || [];
  const baseTables = tables?.pages?.[0]?.tables || [];
  const extraPartnerTables = [
    ...cltTables,
    ...crefazTables,
    ...salvcarTables,
    ...panTables,
    ...crefisaTables,
    ...c6Tables,
    ...crefisaInssTables,
    ...crefazCltTables,
  ];
  const allTables = [
    ...baseTables,
    ...extraPartnerTables.filter(
      (extra) => !baseTables.some((t: any) => t._id === extra._id),
    ),
  ];

  const getSupervisorCommissionForTable = (
    tableId: string | undefined,
    partnerType: PartnerType,
    valueIndex?: number,
  ): number | undefined => {
    if (!supervisorData?.commissionsV2) return undefined;

    const partnerConfig = supervisorData.commissionsV2[partnerType];
    if (!partnerConfig?.items) return undefined;

    if (partnerType === PartnerType.FGTS) {
      const item = partnerConfig.items.find((i: any) => i._id === tableId);
      return item?.supervisorCommission;
    }

    if (valueIndex !== undefined && partnerConfig.items[valueIndex]) {
      return partnerConfig.items[valueIndex]?.supervisorCommission;
    }

    return partnerConfig.items[0]?.supervisorCommission;
  };

  const getPartnerTypeFromTableName = (tableName?: string): PartnerType => {
    const lower = tableName?.toLowerCase() || "";
    if (lower === "clt") return PartnerType.CLT;
    if (lower === "crefaz_clt") return PartnerType.CREFAZ_CLT;
    if (lower.includes("crefaz")) return PartnerType.CREFAZ;
    if (lower.includes("salvcar")) return PartnerType.SALVCAR;
    if (lower.includes("crefisa") && lower.includes("inss"))
      return PartnerType.CREFISA_INSS;
    if (lower.includes("crefisa")) return PartnerType.CREFISA;
    if (lower.includes("pan")) return PartnerType.PAN;
    if (lower.includes("c6")) return PartnerType.C6;
    return PartnerType.FGTS;
  };

  const fullTableNameForType = (item: Commission): string | undefined => {
    if (item.type) return item.type;
    return item.name;
  };

  const existingNames = new Set(
    (localCommission || []).map((c) => c.name).filter(Boolean),
  );
  const missingCltTables = (allTables || [])
    .filter(
      (t: any) =>
        t.isActive !== false &&
        t.type?.toUpperCase() === "CLT" &&
        !existingNames.has(t.name),
    )
    .map((t: any) => ({
      table: t._id,
      name: t.name,
      type: t.type,
      max: t.maxPercents ?? 0,
      seller: undefined,
      leader: undefined,
      supervisor: undefined,
    })) as Commission[];

  const mergedCommission = [...(localCommission || []), ...missingCltTables];

  const commissionWithMax = mergedCommission
    .map((item) => {
      const matchingTable = defaultTablesCommission?.find(
        (table) => table.label === item.name,
      );
      const itemTableId = (item.table as any)?.toString?.() ?? item.table;
      const tableNameForType = fullTableNameForType(item);
      const partnerType = getPartnerTypeFromTableName(tableNameForType);
      const partnerConfigTableId =
        productsConfig?.products?.[partnerType]?.tableId ??
        productsConfig?.products?.[partnerType]?._id;
      const fullTable =
        allTables.find(
          (t: any) => t._id?.toString() === itemTableId || t.name === item.name,
        ) ??
        allTables.find(
          (t: any) =>
            partnerConfigTableId &&
            t._id?.toString() === partnerConfigTableId?.toString(),
        ) ??
        allTables.find((t: any) => t.type?.toUpperCase() === partnerType);
      const tableNameLower =
        fullTable?.name?.toLowerCase() || item.name?.toLowerCase() || "";
      const isCrefazOrSalvcar =
        tableNameLower.includes("crefaz") || tableNameLower.includes("salvcar");
      const isCrefisaOrPan =
        tableNameLower.includes("crefisa") || tableNameLower.includes("pan");
      const isC6 = tableNameLower.includes("c6");
      const isClt =
        partnerType === PartnerType.CLT ||
        fullTable?.type?.toUpperCase() === PartnerType.CLT;
      const isSpecialTable =
        isCrefazOrSalvcar || isCrefisaOrPan || isC6 || isClt;
      const tableId = fullTable?._id?.toString();

      if (
        isSpecialTable &&
        fullTable?.valuesAndCommissions &&
        fullTable.valuesAndCommissions.length > 0
      ) {
        const tableValues = fullTable.valuesAndCommissions;
        const tableSupervisorCommission =
          (fullTable as any)?.supervisorCommission ?? 0;
        const isSingleCommissionPartner =
          partnerType === PartnerType.CREFISA ||
          partnerType === PartnerType.PAN ||
          partnerType === PartnerType.C6 ||
          partnerType === PartnerType.CREFAZ_CLT;

        const values = tableValues.map((tableValue: any, index: number) => {
          const existingValue = item.values?.[index];
          const resolvedMax = isSingleCommissionPartner
            ? (fullTable?.maxPercents ?? tableValue.max)
            : tableValue.max;

          const placeholderSeller =
            tableValue.commission ?? tableValue.percents ?? 0;
          const placeholderLeader = tableValue.leaderCommission ?? 0;
          const supervisorFromProfile = getSupervisorCommissionForTable(
            tableId,
            partnerType,
            index,
          );
          const placeholderSupervisor =
            supervisorFromProfile ??
            tableValue.supervisorCommission ??
            tableSupervisorCommission ??
            0;

          if (existingValue) {
            return {
              ...existingValue,
              value: tableValue.value,
              max: resolvedMax,
              placeholderSeller,
              placeholderLeader,
              placeholderSupervisor,
            };
          }

          return {
            value: tableValue.value,
            seller: item.seller ?? undefined,
            leader: item.leader ?? undefined,
            supervisor: item.supervisor ?? undefined,
            max: resolvedMax,
            placeholderSeller,
            placeholderLeader,
            placeholderSupervisor,
          };
        });

        return {
          ...item,
          type: fullTable?.type,
          max: 0,
          values,
          placeholderSeller: values[0]?.placeholderSeller,
          placeholderLeader: values[0]?.placeholderLeader,
          placeholderSupervisor: values[0]?.placeholderSupervisor,
        };
      }

      const placeholderSeller = fullTable?.percents ?? 0;
      const placeholderLeader = (fullTable as any)?.leaderCommission ?? 0;
      const supervisorFromProfile = getSupervisorCommissionForTable(
        tableId,
        partnerType,
      );
      const placeholderSupervisor =
        supervisorFromProfile ?? (fullTable as any)?.supervisorCommission ?? 0;

      const enrichedValues = item.values?.length
        ? item.values.map((v: any) => ({
            ...v,
            placeholderSeller: v.placeholderSeller ?? placeholderSeller,
            placeholderLeader: v.placeholderLeader ?? placeholderLeader,
            placeholderSupervisor:
              v.placeholderSupervisor ?? placeholderSupervisor,
          }))
        : undefined;

      return {
        ...item,
        type: fullTable?.type,
        seller: item.seller ?? undefined,
        leader: item.leader ?? undefined,
        supervisor: item.supervisor ?? undefined,
        max: matchingTable?.max ?? fullTable?.maxPercents ?? 0,
        placeholderSeller,
        placeholderLeader,
        placeholderSupervisor,
        ...(enrichedValues && { values: enrichedValues }),
      };
    })
    .sort((a, b) => {
      const aHasValue =
        (a.leader !== undefined && a.leader !== 0) ||
        (a.seller !== undefined && a.seller !== 0) ||
        (a.supervisor !== undefined && a.supervisor !== 0);
      const bHasValue =
        (b.leader !== undefined && b.leader !== 0) ||
        (b.seller !== undefined && b.seller !== 0) ||
        (b.supervisor !== undefined && b.supervisor !== 0);
      return aHasValue === bHasValue ? 0 : aHasValue ? -1 : 1;
    });

  const { mutateAsync, isPending } = useMutation({
    mutationKey: ["updateTeamTables"],
    mutationFn: async ({ data, id }: { data: UpdateTeamdata; id: string }) => {
      return teamsService.updateTeam(data, id);
    },
    onSuccess() {
      queryClient.invalidateQueries({ queryKey: ["teamId", id] });
      queryClient.resetQueries({ queryKey: ["Allteams", {}] });
    },
  });

  const handleChange = (
    index: number,
    type: "seller" | "leader" | "supervisor",
    value: number | undefined,
    valueIndex?: number,
  ) => {
    const updated = [...commissionWithMax];

    if (valueIndex !== undefined && updated[index].values) {
      const updatedValues = [...(updated[index].values || [])];
      updatedValues[valueIndex] = {
        ...updatedValues[valueIndex],
        [type]: value,
      };
      (updated[index] as any) = {
        ...updated[index],
        values: updatedValues,
      };
    } else {
      updated[index] = {
        ...updated[index],
        [type]: value,
      };
    }

    setLocalCommission(updated);
  };

  const hasValidationError = commissionWithMax.some((table) => {
    if (table.values && table.values.length > 0) {
      return table.values.some((v: any) => {
        const sum = (v.seller || 0) + (v.leader || 0) + (v.supervisor || 0);
        const max = v.max || 0;
        return max > 0 && sum > max;
      });
    }
    const sum =
      (table.seller || 0) + (table.leader || 0) + (table.supervisor || 0);
    const max = table.max || 0;
    return max > 0 && sum > max;
  });

  const handleSave = async () => {
    if (!id) return;

    const modified = commissionWithMax?.filter((table) => {
      const original = (initialCommission || []).find(
        (c) => c.name === table.name,
      );

      if (table.values && table.values.length > 0) {
        if (!original?.values || original.values.length === 0) {
          return table.values.some(
            (v: any) => v.seller !== 0 || v.leader !== 0 || v.supervisor !== 0,
          );
        }

        return table.values.some((v: any, i: number) => {
          const origValue = original.values?.[i];
          return (
            !origValue ||
            v.seller !== origValue.seller ||
            v.leader !== origValue.leader ||
            v.supervisor !== origValue.supervisor
          );
        });
      }

      if (!original) {
        return (
          (table.seller || 0) > 0 ||
          (table.leader || 0) > 0 ||
          (table.supervisor || 0) > 0
        );
      }

      return (
        table.seller !== original.seller ||
        table.leader !== original.leader ||
        table.supervisor !== original.supervisor
      );
    });

    if (modified.length === 0) {
      toast({ title: "Nenhuma alteração detectada." });
      return;
    }

    const cleanedCommission = modified.map((item) => {
      const fullTable = allTables.find((t: any) => t.name === item.name);

      const cleanedValues = item.values?.map((v: any) => ({
        value: v.value,
        seller: v.seller,
        leader: v.leader,
        supervisor: v.supervisor,
      }));

      return {
        table: item.table,
        type: fullTable?.type,
        seller: item.seller,
        leader: item.leader,
        supervisor: item.supervisor,
        ...(cleanedValues &&
          cleanedValues.length > 0 && { values: cleanedValues }),
      };
    });

    try {
      await mutateAsync({ data: { commissionV2: cleanedCommission }, id });
      // console.log({ data: { commission: cleanedCommission }, id })
      toast({ title: "Tabelas atualizadas com sucesso!" });
      setOpen(false);
    } catch (err: any) {
      const apiMessage =
        err?.message || err?.data?.message || "Erro ao atualizar as tabelas.";

      toast({
        variant: "destructive",
        title: "Erro ao atualizar as tabelas.",
        description: apiMessage,
      });
    }
  };

  return {
    open,
    setOpen,
    localCommission,
    commissionWithMax,
    handleChange,
    handleSave,
    isPending,
    productsConfig,
    hasValidationError,
  };
};
